<script setup>
// Lead Workspace — Tasks tab. Backed by the existing Nexceo::HumanTask model
// (M26.12), now exposed via the new thin Nexceo::LeadTasksController — the
// same task record type AI-originated callbacks/site-visit follow-ups
// already create, just with a REST surface for agents to manage manually.
// No second task system. AI Task Suggestion / Next Best Actions shown in the
// reference have no backing recommendation engine here, so they're omitted
// rather than faked.
import { ref, computed, onMounted } from 'vue';
import { useAlert } from 'dashboard/composables';
import leadsAPI from 'dashboard/api/nexceo/leads';
import { formatDateTime, humanize } from '../leadFormatters';

const props = defineProps({
  lead: { type: Object, required: true },
});

const TASK_TYPES = [
  { value: 'callback', label: 'Callback' },
  { value: 'meeting', label: 'Meeting' },
  { value: 'site_visit', label: 'Site Visit' },
  { value: 'finance_discussion', label: 'Finance Discussion' },
  { value: 'documentation_request', label: 'Documentation' },
  { value: 'booking_assistance', label: 'Booking Assistance' },
  { value: 'other', label: 'Other' },
];

const tasks = ref([]);
const isLoading = ref(true);
const activeView = ref('all');
const isFormOpen = ref(false);
const selectedTask = ref(null);

const newTask = ref({ reason: '', task_type: 'callback', priority: 'normal', due_at: '' });
const isSaving = ref(false);

async function load() {
  isLoading.value = true;
  try {
    const { data } = await leadsAPI.getTasks(props.lead.id);
    tasks.value = data ?? [];
  } catch {
    tasks.value = [];
  } finally {
    isLoading.value = false;
  }
}
onMounted(load);

const summary = computed(() => ({
  total: tasks.value.length,
  completed: tasks.value.filter(t => t.status === 'completed').length,
  in_progress: tasks.value.filter(t => t.status === 'in_progress').length,
  pending: tasks.value.filter(t => t.status === 'pending').length,
  overdue: tasks.value.filter(t => t.status === 'overdue' || t.overdue_computed).length,
}));

const VIEWS = [
  { key: 'all', label: 'All Tasks' },
  { key: 'open', label: 'Open' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'completed', label: 'Completed' },
];

const activeCategory = ref('');

const categoryCounts = computed(() => {
  const counts = {};
  tasks.value.forEach(t => {
    counts[t.task_type] = (counts[t.task_type] || 0) + 1;
  });
  return counts;
});

const filteredTasks = computed(() => {
  let list = tasks.value;
  if (activeView.value === 'open') list = list.filter(t => ['pending', 'in_progress'].includes(t.status));
  else if (activeView.value === 'overdue') list = list.filter(t => t.status === 'overdue' || t.overdue_computed);
  else if (activeView.value === 'completed') list = list.filter(t => t.status === 'completed');
  if (activeCategory.value) list = list.filter(t => t.task_type === activeCategory.value);
  return list;
});

const PRIORITY_CLS = {
  urgent: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
  high: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  normal: 'bg-woot-100 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400',
  low: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400',
};
const STATUS_CLS = {
  pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/15 dark:text-yellow-400',
  in_progress: 'bg-woot-100 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400',
  completed: 'bg-green-100 text-green-700 dark:bg-green-500/15 dark:text-green-400',
  cancelled: 'bg-slate-100 text-slate-600 dark:bg-slate-500/15 dark:text-slate-400',
  overdue: 'bg-red-100 text-red-700 dark:bg-red-500/15 dark:text-red-400',
};

async function createTask() {
  if (!newTask.value.reason.trim()) return;
  isSaving.value = true;
  try {
    await leadsAPI.createTask(props.lead.id, {
      reason: newTask.value.reason.trim(),
      task_type: newTask.value.task_type,
      priority: newTask.value.priority,
      due_at: newTask.value.due_at || null,
    });
    useAlert('Task created.');
    isFormOpen.value = false;
    newTask.value = { reason: '', task_type: 'callback', priority: 'normal', due_at: '' };
    load();
  } catch {
    useAlert('Failed to create task.');
  } finally {
    isSaving.value = false;
  }
}

async function completeTask(task) {
  try {
    await leadsAPI.completeTask(props.lead.id, task.id);
    useAlert('Task marked complete.');
    load();
  } catch {
    useAlert('Failed to complete task.');
  }
}

async function cancelTask(task) {
  try {
    await leadsAPI.cancelTask(props.lead.id, task.id);
    useAlert('Task cancelled.');
    load();
  } catch {
    useAlert('Failed to cancel task.');
  }
}
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="p-4 space-y-4">
    <!-- Summary strip -->
    <div class="grid grid-cols-2 sm:grid-cols-5 gap-2">
      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-lg font-semibold text-n-slate-12">{{ summary.total }}</p>
        <p class="text-xs text-n-slate-8">Total Tasks</p>
      </div>
      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-lg font-semibold text-green-600">{{ summary.completed }}</p>
        <p class="text-xs text-n-slate-8">Completed</p>
      </div>
      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-lg font-semibold text-woot-600">{{ summary.in_progress }}</p>
        <p class="text-xs text-n-slate-8">In Progress</p>
      </div>
      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-lg font-semibold text-yellow-600">{{ summary.pending }}</p>
        <p class="text-xs text-n-slate-8">Pending</p>
      </div>
      <div class="rounded-lg border border-n-weak p-3">
        <p class="text-lg font-semibold text-red-600">{{ summary.overdue }}</p>
        <p class="text-xs text-n-slate-8">Overdue</p>
      </div>
    </div>

    <div class="grid grid-cols-1 lg:grid-cols-[180px_1fr_280px] gap-4">
      <!-- Views -->
      <div class="rounded-xl border border-n-weak bg-n-background p-3 h-fit">
        <h3 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide px-1 mb-2">
          Task Views
        </h3>
        <button
          v-for="v in VIEWS"
          :key="v.key"
          class="w-full text-left px-2 py-1.5 rounded-lg text-sm"
          :class="
            activeView === v.key
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activeView = v.key"
        >
          {{ v.label }}
        </button>

        <h3 class="text-xs font-semibold text-n-slate-9 uppercase tracking-wide px-1 mt-4 mb-2">
          Task Categories
        </h3>
        <button
          class="w-full text-left px-2 py-1.5 rounded-lg text-sm"
          :class="
            !activeCategory
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activeCategory = ''"
        >
          All ({{ tasks.length }})
        </button>
        <button
          v-for="t in TASK_TYPES"
          :key="t.value"
          v-show="categoryCounts[t.value]"
          class="w-full text-left px-2 py-1.5 rounded-lg text-sm"
          :class="
            activeCategory === t.value
              ? 'bg-woot-50 text-woot-700 dark:bg-woot-500/15 dark:text-woot-400 font-medium'
              : 'text-n-slate-11 hover:bg-n-alpha-1'
          "
          @click="activeCategory = t.value"
        >
          {{ t.label }} ({{ categoryCounts[t.value] || 0 }})
        </button>
      </div>

      <!-- Task list -->
      <div class="rounded-xl border border-n-weak bg-n-background p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ VIEWS.find(v => v.key === activeView)?.label }} ({{ filteredTasks.length }})
          </h3>
          <button
            class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg hover:bg-woot-600 font-medium"
            @click="isFormOpen = !isFormOpen"
          >
            <span class="i-lucide-plus size-3.5" /> Add Task
          </button>
        </div>

        <div v-if="isFormOpen" class="rounded-lg border border-n-weak p-3 mb-3 space-y-2">
          <input
            v-model="newTask.reason"
            type="text"
            placeholder="What needs to be done?"
            class="!border !border-solid !border-n-weak rounded-lg px-3 py-2 text-sm w-full bg-n-background text-n-slate-12 outline-none focus:!border-woot-500"
          />
          <div class="grid grid-cols-3 gap-2">
            <select
              v-model="newTask.task_type"
              class="!border !border-solid !border-n-weak rounded-lg px-2 py-1.5 text-sm bg-n-background text-n-slate-12"
            >
              <option v-for="t in TASK_TYPES" :key="t.value" :value="t.value">{{ t.label }}</option>
            </select>
            <select
              v-model="newTask.priority"
              class="!border !border-solid !border-n-weak rounded-lg px-2 py-1.5 text-sm bg-n-background text-n-slate-12 capitalize"
            >
              <option v-for="p in ['low', 'normal', 'high', 'urgent']" :key="p" :value="p">{{ p }}</option>
            </select>
            <input
              v-model="newTask.due_at"
              type="datetime-local"
              class="!border !border-solid !border-n-weak rounded-lg px-2 py-1.5 text-sm bg-n-background text-n-slate-12"
            />
          </div>
          <div class="flex justify-end gap-2">
            <button class="text-xs px-3 py-1.5 text-n-slate-9" @click="isFormOpen = false">Cancel</button>
            <button
              :disabled="isSaving || !newTask.reason.trim()"
              class="text-xs px-3 py-1.5 rounded-lg bg-woot-500 text-white disabled:opacity-50"
              @click="createTask"
            >
              {{ isSaving ? 'Saving…' : 'Create Task' }}
            </button>
          </div>
        </div>

        <div v-if="isLoading" class="text-sm text-n-slate-8">Loading…</div>
        <p v-else-if="!filteredTasks.length" class="text-sm text-n-slate-8">No tasks here.</p>
        <ul v-else class="divide-y divide-n-weak">
          <li
            v-for="task in filteredTasks"
            :key="task.id"
            class="py-2.5 flex items-start gap-2 cursor-pointer"
            @click="selectedTask = task"
          >
            <span
              class="size-4 rounded border shrink-0 mt-0.5 flex items-center justify-center"
              :class="task.status === 'completed' ? 'bg-green-500 border-green-500' : 'border-n-weak'"
            >
              <span v-if="task.status === 'completed'" class="i-lucide-check size-3 text-white" />
            </span>
            <div class="min-w-0 flex-1">
              <p class="text-sm text-n-slate-12" :class="{ 'line-through text-n-slate-8': task.status === 'completed' }">
                {{ task.reason }}
              </p>
              <p class="text-xs text-n-slate-8">
                {{ humanize(task.task_type) }}<template v-if="task.due_at"> · Due {{ formatDateTime(task.due_at) }}</template>
              </p>
            </div>
            <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" :class="PRIORITY_CLS[task.priority]">
              {{ humanize(task.priority) }}
            </span>
            <span class="text-[10px] font-semibold px-1.5 py-0.5 rounded-full shrink-0" :class="STATUS_CLS[task.overdue_computed ? 'overdue' : task.status]">
              {{ task.overdue_computed && task.status !== 'completed' ? 'Overdue' : humanize(task.status) }}
            </span>
          </li>
        </ul>
      </div>

      <!-- Task details -->
      <div class="rounded-xl border border-n-weak bg-n-background p-4 h-fit">
        <h3 class="text-sm font-semibold text-n-slate-12 mb-2">Task Details</h3>
        <div v-if="!selectedTask" class="text-xs text-n-slate-8">Select a task to see details.</div>
        <div v-else class="space-y-2 text-sm">
          <p class="font-medium text-n-slate-12">{{ selectedTask.reason }}</p>
          <dl class="space-y-1.5">
            <div class="flex justify-between text-xs">
              <dt class="text-n-slate-8">Type</dt>
              <dd class="text-n-slate-12">{{ humanize(selectedTask.task_type) }}</dd>
            </div>
            <div class="flex justify-between text-xs">
              <dt class="text-n-slate-8">Assigned To</dt>
              <dd class="text-n-slate-12">{{ selectedTask.owner?.name || 'Unassigned' }}</dd>
            </div>
            <div class="flex justify-between text-xs">
              <dt class="text-n-slate-8">Due</dt>
              <dd class="text-n-slate-12">{{ formatDateTime(selectedTask.due_at) || '—' }}</dd>
            </div>
            <div class="flex justify-between text-xs">
              <dt class="text-n-slate-8">Status</dt>
              <dd class="text-n-slate-12">{{ humanize(selectedTask.status) }}</dd>
            </div>
          </dl>
          <div v-if="!['completed', 'cancelled'].includes(selectedTask.status)" class="flex gap-2 pt-2">
            <button
              class="flex-1 text-xs py-1.5 rounded-lg bg-green-500 text-white"
              @click="completeTask(selectedTask)"
            >
              Complete
            </button>
            <button
              class="flex-1 text-xs py-1.5 rounded-lg border border-n-weak text-n-slate-11"
              @click="cancelTask(selectedTask)"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
